import React, { useState, useEffect } from 'react';
import { track } from '@vercel/analytics';
import { submitUserData } from '../utils/firebaseSubmission';

export default function UserDataForm({ onComplete, onSkip, level, misses, gameNumber, sessionScore, sessionMisses, firebaseDocId, initialUserData, currentLanguage, translationFunction }) {
  const t = translationFunction; // Use passed translation function instead of hook
  const [step, setStep] = useState('question'); // Skip 'userInfo' and go directly to 'question'
  const [userName, setUserName] = useState(initialUserData?.userName || '');
  const [userEmail, setUserEmail] = useState(initialUserData?.userEmail || '');
  const [questionAnswer, setQuestionAnswer] = useState(initialUserData?.questionAnswer || '');
  const [privacyAccepted, setPrivacyAccepted] = useState(initialUserData?.privacyAccepted || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Update form fields when initialUserData changes (for subsequent games)
  useEffect(() => {
    if (initialUserData && initialUserData.userName) {
      setUserName(initialUserData.userName || '');
      setUserEmail(initialUserData.userEmail || '');
      setQuestionAnswer(initialUserData.questionAnswer || '');
      setPrivacyAccepted(initialUserData.privacyAccepted || false);
    }
  }, [initialUserData]);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isUserInfoComplete = userName.trim() !== '' && userEmail.trim() !== '' && isValidEmail(userEmail) && privacyAccepted;
  const isQuestionComplete = questionAnswer.trim() !== '';
  const isAllComplete = isUserInfoComplete && isQuestionComplete;

  // Get badge feedback text based on level (same logic as badge generation)
  const getBadgeFeedback = (level) => {
    if (level >= 12) {
      return t('badge.achievement12');
    } else if (level >= 10) {
      return t('badge.achievement10');
    } else if (level >= 7) {
      return t('badge.achievement7');
    } else if (level >= 4) {
      return t('badge.achievement4');
    } else {
      return t('badge.achievement1');
    }
  };

  const getPercentText = (level) => {
    if (level >= 12) {
      return t('badge.percent12');
    } else if (level >= 10) {
      return t('badge.percent10');
    } else if (level >= 7) {
      return t('badge.percent7');
    } else if (level >= 4) {
      return t('badge.percent4');
    } else {
      return t('badge.percent1');
    }
  };

  const handleContinue = async () => {
    if (step === 'userInfo') {
      setStep('question');
    } else if (step === 'question' && isAllComplete) {
      // Submit data to Firebase when moving from question to complete
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        const userData = {
          userName: userName.trim(),
          userEmail: userEmail.trim(),
          questionAnswer: questionAnswer.trim(),
          level: level,
          misses: misses,
          gameNumber: gameNumber,
          sessionScore: sessionScore,
          sessionMisses: sessionMisses,
          privacyConsent: privacyAccepted,
          completedAt: new Date().toISOString()
        };
        
        // Only submit if we have a Firebase document ID
        if (firebaseDocId) {
          await submitUserData(firebaseDocId, userData);
        }
        
        setStep('complete');
        
        // Call onComplete with user data for the score display
        onComplete({
          userName: userName.trim(),
          userEmail: userEmail.trim(),
          questionAnswer: questionAnswer.trim()
        });
      } catch (error) {
        console.error('Failed to submit user data:', error);
        setSubmitError('Failed to save your information. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSkip = () => {
    track('SKIP_USER_DATA_COLLECTION');
    onSkip();
  };

  if (step === 'complete') {
    return null; // Form is complete, let parent handle display
  }

  return (
    <>
      {step === 'userInfo' && (
        <>
          {/* Achievement info only - Mission Complete text moved to question step */}
          <div className="form-header">
            <div className="ui-congrats-attempts">
              {t('gameUI.levelReached').replace('{level}', level)}
            </div>
            <div className="badge-feedback">
              {getBadgeFeedback(level)}
            </div>
            <div className="badge-percent">
              {getPercentText(level)}
            </div>
          </div>

          <div className="launch-form-container">
            <button 
              className="ui-congrats-btn"
              onClick={handleContinue}
            >
              {t('launchMenu.continue')}
            </button>
          </div>
        </>
      )}

      {step === 'question' && (
        <div className="launch-form-container">
          {/* Mission Complete Header - moved from userInfo step */}
          <div className="form-header no-margin">
            <div className="congrats-message">
              {t('gameUI.missionComplete') || 'Mission Complete!'}
            </div>
          </div>
          
          {/* Question Header */}
          <div className="form-header no-margin">
            <div className="congrats-message crew-invitation-text">
              {t('launchMenu.questionLabel').split('\n\n')[0]}
            </div>
            <div className="question-body">
              {t('launchMenu.questionLabel').split('\n\n').slice(1).join('\n\n')}
            </div>
          </div>
          
          <div className="form-field">
            <textarea
              id="questionAnswer"
              value={questionAnswer}
              onChange={(e) => setQuestionAnswer(e.target.value)}
              className="form-input form-textarea question-answer-mobile"
              placeholder={t('launchMenu.questionPlaceholder')}
              maxLength={150}
              rows={typeof window !== 'undefined' && window.innerWidth <= 600 ? 2 : 4}
            />
          </div>
          
          <div className="form-field">
            <input
              id="userName"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="form-input"
              placeholder={t('launchMenu.namePlaceholder')}
            />
          </div>
          
          <div className="form-field">
            <input
              id="userEmail"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className={`form-input ${userEmail.trim() !== '' && !isValidEmail(userEmail) ? 'form-input-error' : ''}`}
              placeholder={t('launchMenu.emailPlaceholder')}
            />
            {userEmail.trim() !== '' && !isValidEmail(userEmail) && (
              <span className="form-error-message">{t('launchMenu.emailInvalidError')}</span>
            )}
          </div>
          
          <div className="form-field privacy-checkbox-container">
            <label className="privacy-checkbox-label">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="privacy-checkbox"
              />
              <span className="privacy-text">
                {currentLanguage === 'es' ? (
                  <>
                    He leído y acepto la{' '}
                    <a 
                      href="https://www.pldspace.com/politica-privacidad.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="privacy-link"
                    >
                      política de privacidad
                    </a>
                  </>
                ) : (
                  <>
                    I have read and accept the{' '}
                    <a 
                      href="https://pld-space.wamdev.net/en/privacy-policy.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="privacy-link"
                    >
                      privacy policy
                    </a>
                  </>
                )}
              </span>
            </label>
          </div>
          
          {submitError && (
            <div className="form-error-message submit-error">
              {submitError}
            </div>
          )}
          
          <button 
            className="ui-congrats-btn"
            onClick={handleContinue}
            disabled={!isAllComplete || isSubmitting}
          >
            {isSubmitting ? (t('launchMenu.submitting') || 'Submitting...') : (t('launchMenu.submit') || 'Submit')}
          </button>
        </div>
      )}
    </>
  );
}